import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Upload, Download, CheckCircle2, AlertCircle, Loader2,
  FileText, Trash2, Activity, Droplets, ShieldAlert, Percent, TrendingUp,
} from 'lucide-react';

import { Button }       from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiErrorBanner } from '@/features/parameters/ApiErrorBanner';
import { useImportPositions, useClearPositions, downloadTemplate } from './api';
import { PRODUCT_TYPES, ProductType, ImportResult } from './types';

const TABS: ProductType[] = ['bonds', 'loans', 'deposits', 'interbank', 'offbalance'];

// ── Engine result row ─────────────────────────────────────────────────────────

function EngineRow({
  label, icon: Icon, resultId, error, href,
}: {
  label:    string;
  icon:     React.ComponentType<{ className?: string }>;
  resultId: string | null | undefined;
  error:    string | null | undefined;
  href:     string;
}) {
  if (!resultId && !error) return null;
  return (
    <div className="flex items-center gap-2 text-sm">
      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
      <span className="font-medium w-20 shrink-0">{label}</span>
      {resultId ? (
        <>
          <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
          <Link to={href} className="text-primary underline-offset-4 hover:underline truncate">
            View result
          </Link>
        </>
      ) : (
        <>
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <span className="text-destructive truncate" title={error ?? ''}>
            {error}
          </span>
        </>
      )}
    </div>
  );
}

// ── Import result summary card ────────────────────────────────────────────────

function ImportSummaryCard({ result }: { result: ImportResult }) {
  const anyError = result.gapError || result.nimError || result.lcrError || result.eveError;
  return (
    <Card className={anyError ? 'border-yellow-400' : 'border-green-400'}>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          {anyError
            ? <AlertCircle className="h-5 w-5 text-yellow-500" />
            : <CheckCircle2 className="h-5 w-5 text-green-500" />}
          <CardTitle className="text-base">
            Import complete — {result.asOfDate}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Gold build stats */}
        <div className="flex flex-wrap gap-4 text-sm">
          <span>
            <span className="font-semibold">{result.rowsImported}</span> rows imported
          </span>
          <span>
            <span className="font-semibold">{result.gapCfRowsBuilt}</span> gap CF rows built
          </span>
          <span>
            <span className="font-semibold">{result.lcrRowsBuilt}</span> LCR rows built
          </span>
        </div>

        {/* Per-engine results */}
        <div className="space-y-1.5 pt-1 border-t">
          <EngineRow label="Gap"      icon={TrendingUp}  resultId={result.gapResultId} error={result.gapError} href="/alm/gaps" />
          <EngineRow label="NIM"      icon={Percent}     resultId={result.nimResultId} error={result.nimError} href="/alm/nim" />
          <EngineRow label="LCR"      icon={Droplets}    resultId={result.lcrResultId} error={result.lcrError} href="/alm/liquidity" />
          <EngineRow label="EVE/KRD"  icon={ShieldAlert} resultId={result.eveResultId} error={result.eveError} href="/alm/eve" />
        </div>
      </CardContent>
    </Card>
  );
}

// ── Per-product upload tab ────────────────────────────────────────────────────

function ProductUploadTab({ productType }: { productType: ProductType }) {
  const meta      = PRODUCT_TYPES[productType];
  const fileRef   = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);

  const importMut = useImportPositions();

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    importMut.reset();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) return;
    importMut.mutate({ type: productType, file });
  }

  return (
    <div className="space-y-4">
      {/* Description + template */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            {meta.label}
          </CardTitle>
          <CardDescription>{meta.description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <FileText className="h-4 w-4 shrink-0" />
            <span>Instrument types: <code className="bg-muted px-1 rounded text-xs">{meta.instruments}</code></span>
          </div>
          <div className="mt-3">
            <button
              type="button"
              onClick={() => downloadTemplate(productType)}
              className="inline-flex items-center gap-1.5 text-sm text-primary underline-offset-4 hover:underline"
            >
              <Download className="h-4 w-4" />
              Download template ({meta.templateFile})
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Upload form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex flex-col gap-2">
          <label
            htmlFor={`file-${productType}`}
            className="text-sm font-medium"
          >
            Select CSV file
          </label>
          <input
            id={`file-${productType}`}
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileChange}
            className="block w-full text-sm text-muted-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-input file:text-sm file:font-medium file:bg-background file:text-foreground hover:file:bg-muted cursor-pointer"
          />
          {file && (
            <p className="text-xs text-muted-foreground">
              {file.name} — {(file.size / 1024).toFixed(1)} KB
            </p>
          )}
        </div>

        <Button
          type="submit"
          disabled={!file || importMut.isPending}
          className="w-full sm:w-auto"
        >
          {importMut.isPending ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Importing & recalculating…</>
          ) : (
            <><Upload className="mr-2 h-4 w-4" />Import positions & run engines</>
          )}
        </Button>
      </form>

      {/* Results */}
      {importMut.isError && (
        <ApiErrorBanner error={importMut.error} />
      )}
      {importMut.isSuccess && importMut.data && (
        <ImportSummaryCard result={importMut.data} />
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

/**
 * Phase 6.BRONZE-INGEST — Position import page.
 *
 * Five tabs (one per product group), each with a template download + CSV upload.
 * On upload the backend: rebuilds gold → runs all 4 engines → returns result IDs.
 * Uploads accumulate: uploading bonds then loans gives a gold snapshot with both.
 */
export function PositionImportPage() {
  const [activeTab, setActiveTab] = useState<ProductType>('bonds');
  const clearMut = useClearPositions();

  function handleClear() {
    if (!window.confirm(
      'This will delete all manually imported positions for today and wipe the gold snapshot. Continue?'
    )) return;
    clearMut.mutate();
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6 p-6">

      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Position Import</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Upload product positions to validate calculation results. Each upload
            rebuilds the gold layer and auto-triggers all 4 ALM engines.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0 text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground"
          onClick={handleClear}
          disabled={clearMut.isPending}
        >
          {clearMut.isPending
            ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Clearing…</>
            : <><Trash2 className="mr-1.5 h-3.5 w-3.5" />Clear all positions</>
          }
        </Button>
      </div>

      {/* Workflow callout */}
      <Card className="bg-muted/50 border-dashed">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-start gap-3 text-sm">
            <Activity className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
            <p className="text-muted-foreground">
              Uploads <strong>accumulate</strong> — importing bonds then loans gives a
              complete gold snapshot covering both product groups. Re-uploading a product
              type replaces only that group's rows before rebuilding gold.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Clear result */}
      {clearMut.isSuccess && (
        <div className="flex items-center gap-2 text-sm text-green-600">
          <CheckCircle2 className="h-4 w-4" />
          All positions cleared for {clearMut.data?.asOfDate}.
        </div>
      )}
      {clearMut.isError && <ApiErrorBanner error={clearMut.error} />}

      {/* Product tabs */}
      <div>
        <div
          role="tablist"
          className="flex gap-1 flex-wrap border-b mb-5"
          aria-label="Product type"
        >
          {TABS.map(tab => (
            <button
              key={tab}
              role="tab"
              aria-selected={activeTab === tab}
              onClick={() => setActiveTab(tab)}
              className={[
                'px-4 py-2 text-sm font-medium rounded-t transition-colors',
                activeTab === tab
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-muted-foreground hover:text-foreground',
              ].join(' ')}
            >
              {PRODUCT_TYPES[tab].label}
            </button>
          ))}
        </div>

        <ProductUploadTab key={activeTab} productType={activeTab} />
      </div>
    </div>
  );
}
